import { buildRoute } from "fetch-run";
import { useCallback, useEffect } from "react";

import { executeQuery, invalidateQuery, useApiStore } from "./store";
import { ExtractRouteParams, WithOptionalRouteParams } from "./types";

export type Query<T> = {
  data: T | undefined;
  error: Error | null;
  invalidate: () => void;
  isFetching: boolean;
  isLoading: boolean;
  refetch: () => Promise<void>;
};

// Base function for shared query logic
function useQueryBase<Res>(
  ns: string,
  idResolver: () => string,
  execute: (url: string) => Promise<Res | undefined>
): Query<Res> {
  // Vars
  const id = idResolver();
  const url = id;

  // State
  const data = useApiStore(ns, (s) => s.data[id] as Res | undefined);
  const { error, isFetching, isStale } = useApiStore(ns, (s) => ({
    error: s.errors[id] ?? null,
    isFetching: s.fetching[id] === true,
    isStale: s.stale[id] !== false,
  }));

  // Computed
  const isLoading = !data && isFetching;

  // Methods
  const invalidate = useCallback(() => {
    invalidateQuery(ns, id);
  }, [id, ns]);

  const refetch = useCallback(() => {
    return executeQuery(ns, id, () => execute(url));
    // Missing dependency 'execute' is intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ns, id, url]);

  // Effects
  useEffect(() => {
    if (!error && (!data || isStale)) {
      refetch();
    }
  }, [data, error, isStale, refetch]);

  // Public
  return {
    data,
    error,
    invalidate,
    isFetching,
    isLoading,
    refetch,
  };
}

export function createQuery<Res extends object>(
  ns: string,
  id: string,
  execute: () => Promise<Res | undefined>
) {
  const useQuery = () => {
    return useQueryBase(ns, () => id, execute);
  };

  return useQuery;
}

export function createRouteQuery<R extends string, Res extends object>(
  ns: string,
  route: R,
  execute: (url: string) => Promise<Res | undefined>
) {
  type _RouteParams = ExtractRouteParams<R>;

  const useQuery = (
    routeParams: _RouteParams = {} as _RouteParams,
    searchParams?: URLSearchParams
  ) => {
    return useQueryBase(
      ns,
      () => {
        // ID resolver for route-based queries
        const id = buildRoute(route, routeParams);
        return !searchParams ? id : `${id}?${searchParams}`;
      },
      execute
    );
  };

  return useQuery as WithOptionalRouteParams<_RouteParams, Query<Res>>;
}
